import { Field, useField, useForm } from 'react-final-form';
import styles from './BankStatements.module.scss';
import OutlinedFormInput from '../../Finance/components/OutlinedFormInput/OutlinedFormInput';
import { onBlurFormatNumber, parseNumberForDB } from '../../../components/functions/utilities';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import MeritoButton from '../../../components/common/MeritoButtons/MeritoButtons';
import API from '../../../rest/api';
import CircularLoader from '../../../components/common/CircularLoader/CircularLoader';
import { RiArrowGoBackFill as BackIcon } from 'react-icons/ri'
import { UnderwritingFormContext, bankStatementFileInitialState } from '../context/UnderwritingFormContext';
import { RiDeleteBin6Line as DeleteIcon } from 'react-icons/ri';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import { FaCircleExclamation as Exclamation } from "react-icons/fa6";
import { ApplicationContextType, GstAnalysisStatus, MonthlyReportSteps, StatementAnalysisStatus, StatementStepType, UnderwritingFormStatus, UnderwritingSteps, uploadStates } from '../../../interfaces/interfaces';
import { getIn } from 'final-form';
import NetBankingFlow from './NetBankingFlow';
import moment from 'moment';
import UploadStatementFlow from './UploadStatementFlow';
import { useBlocker } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { MonthlyReportFormContext } from '../../MonthlyReport/context/MonthlyReportFormContext';

type Props = {
    fieldName: string;
    fieldIndex: number;
    deleteFileKeys: string[];
    setDeleteFileKeys: (keys: string[]) => void;
    newUploadedDocIds: number[];
    setNewUploadedDocIds: React.Dispatch<React.SetStateAction<number[]>>;
    deleteDocIds: number[],
    setDeleteDocIds: React.Dispatch<React.SetStateAction<number[]>>;
    contextType?: ApplicationContextType;
    stepType?: UnderwritingSteps | MonthlyReportSteps;
}

export const getLast12MonthsText = () => {
    const startDate = moment().subtract(12, 'months').startOf("month");
    const endDate = moment().subtract(1, 'months').endOf("month");
    return {
        startDate: startDate,
        endDate: endDate,
        formattedText: `${moment(startDate).format("MMM DD, YYYY")} - ${moment(endDate).format("MMM DD, YYYY")}`
    }
}

export const processingStatus = new Set([StatementAnalysisStatus.processing, StatementAnalysisStatus.verifying]);



const BankStatements = ({ fieldIndex, fieldName, deleteFileKeys, setDeleteFileKeys, deleteDocIds, setDeleteDocIds, newUploadedDocIds, setNewUploadedDocIds, contextType = ApplicationContextType.underwriting, stepType = UnderwritingSteps.documents }: Props) => {
    const fileInputRef = useRef(null);
    const { mutators: { setFieldTouched, remove }, change, batch } = useForm();
    const [showUploadFileState, setShowUploadFileState] = useState(false);
    const [fileUploadLoading, setFileUploadLoading] = useState(false);


    // const { input: { value: uploadedFileValue } } = useField(`${fieldName}.statement.uploadedFile`, { subscription: { value: true } });
    // const { input: { value: selectedFileValue }, meta: selectedFileMeta } = useField(`${fieldName}.statement.file`, { subscription: { value: true, error: true, touched: true } })
    const { input: { value: bankStatements } } = useField(`${fieldName}`, { subscription: { value: true } });

    // console.log('bankStatements', bankStatements);

    const { input: { value: bankStatementValues } } = useField(`bankStatements`, { subscription: { value: true } });

    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

    const { formCompleteStatus, changeUploadStatus, statementAnalysisJobs } = useContext(contextType == ApplicationContextType.monthlyReport ? MonthlyReportFormContext : UnderwritingFormContext);

    const { getState } = useForm();

    const [statementTypeLocal, setStatementLocal] = useState(StatementStepType.net_banking);

    const statementType = getIn(getState()?.values, `${fieldName}.statementType`);
    const statementJobId = getIn(getState()?.values, `${fieldName}.statementJobId`);
    const [isJobProcessing, setIsJobProcessing] = useState(false);
    const [currentAnalysisJob, setCurrentAnalysisJob] = useState(null);


    useEffect(() => {
        // console.log('statementAnalysisJobs', statementAnalysisJobs, 'statementJobId', statementJobId);
        const currentJob = getIn(statementAnalysisJobs, `${statementJobId}.${statementType}`);
        // console.log('currentJob', currentJob);
        if (currentJob) {
            setCurrentAnalysisJob(currentJob);
            if (processingStatus.has(currentJob?.status)) {
                setIsJobProcessing(true);
            } else {
                setIsJobProcessing(false);
            }
        } else {
            setIsJobProcessing(false);
        }
    }, [statementJobId, statementAnalysisJobs]);


    // console.log('isJobProcessing', isJobProcessing);


    useEffect(() => {
        if (statementType) {
            setStatementLocal(statementType);
        }
    }, [statementType]);



    const validateFile = (file) => {
        const allowedFormats = ['application/pdf'];
        const maxFileSize = 20 * 1024 * 1024; // 20 MB

        if (!file) {
            return `Upload bank statement`
        }

        if (!allowedFormats.includes(file.type)) {
            return 'Invalid file format. Please select a PDF file.';
        }
        if (file.size > maxFileSize) {
            return 'File size exceeds 20MB.';
        }
        return null;
    }

    const handleButtonClick = () => {
        fileInputRef.current.click();
    };

    const uploadFile = async (fileData) => {
        try {
            changeUploadStatus(`${fieldName}.statement`, uploadStates.uploading)
            setFileUploadLoading(true);
            console.log('file', fileData);
            const formData = new FormData();
            formData.append('documentType', "bankStatement");
            formData.append('file', fileData);
            const data = await API.post('/app/uploadDocs', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            }).then((res) => res.data);

            console.log('data', data);
            change(`${fieldName}.statement.uploadedFile`, {
                fileKey: data?.Key,
                fileName: fileData?.name,
                fileType: fileData?.type
            })
        } catch (error) {
            console.log('error', error);
        } finally {
            setFileUploadLoading(false);
            changeUploadStatus(`${fieldName}.statement`, uploadStates.doneUploading)
        }
    }

    const processAccountData = useMemo(() => {
        const currentAccount = bankStatementValues[fieldIndex];
        const statements = currentAccount?.statements;
        const statementJobId = currentAccount?.statementJobId;
        let isOldAccount = true;
        if (statements && statements?.length) {
            isOldAccount = statements?.some((statement) => statement?.uploadedFile?.isOldFile);

        } else {
            isOldAccount = false;
        }
        let accountData = null;
        const statementData = currentAccount?.statementData;
        if (statementData) {
            accountData = {
                bankName: statementData?.bank_name,
                accountNumber: statementData?.account_number,
                accountName: statementData?.customer_name
            }
        }
        if (!accountData) {
            const currentJob = getIn(statementAnalysisJobs, `${statementJobId}.${statementType}`);
            // const currentJob = getIn(statementAnalysisJobs, `${statementJobId}`);
            console.log('currentJob for account data', currentJob);
            if (currentJob?.processedData?.account_summaries) {
                accountData = {
                    bankName: currentJob?.processedData?.account_summaries?.bank_name,
                    accountNumber: currentJob?.processedData?.account_summaries?.account_number,
                    accountName: currentJob?.processedData?.account_summaries?.account_name
                }
            } else if (currentJob?.processedData?.summaryData) {
                accountData = {
                    bankName: currentJob?.processedData?.summaryData?.bank_name,
                    accountNumber: currentJob?.processedData?.summaryData?.account_number,
                    accountName: currentJob?.processedData?.summaryData?.customer_name
                }
            }
        }
        return {
            isOldAccount: isOldAccount,
            accountData: accountData
        }
    }, [bankStatementValues, fieldIndex, statementAnalysisJobs]);

    return (
        <>
            <div id={`account_${fieldIndex}`} className={styles.bankStatementFieldsRootContainer}>
                <div className={styles.titleContainer}>
                    <div className={styles.title}>
                        {`Account ${fieldIndex + 1}`}
                    </div>
                    <div className={styles.btnContainer}>
                        {bankStatementValues && bankStatementValues?.length > 1 && !processAccountData?.isOldAccount ?
                            <div>
                                <MeritoButton icon={<DeleteIcon />} type='destructive-ghost' size='md2' onClick={() => {
                                    // fields.remove(index)
                                    // setToBeRemovedIndex(index);
                                    setShowDeleteConfirmation(true);
                                }} />
                            </div> : null}
                    </div>
                </div>
                {processAccountData?.accountData ? <div className={styles.accountSummaryContainer}>
                    <div className={styles.valueContainer}>
                        {`Bank: ${processAccountData?.accountData?.bankName ?? ''}`}
                    </div>
                    <div className={styles.valueContainer}>
                        {`A/c No.: ${processAccountData?.accountData?.accountNumber ?? ''}`}
                    </div>
                    <div className={styles.valueContainer}>
                        {`A/c Name: ${processAccountData?.accountData?.accountName ?? ''}`}
                    </div>
                </div> : null}

                <div className={styles.statementTypeRootContainer}>
                    {/* {statementTypeLocal == StatementStepType.net_banking ?
                        <>
                            <NetBankingFlow fieldName={`${fieldName}`} deleteDocIds={deleteDocIds} />
                            {!isJobProcessing ? <div className={styles.switchStatementText}>
                                If your bank doesn’t support net-banking, then <span onClick={() => {
                                    setStatementLocal(StatementStepType.pdf_upload);
                                }}>upload PDF bank statements</span>.
                            </div> : null}
                        </>
                        :  */}
                    <>
                        <UploadStatementFlow currentAnalysisJob={currentAnalysisJob} isJobProcessing={isJobProcessing} fieldName={fieldName} setDeleteDocIds={setDeleteDocIds} deleteDocIds={deleteDocIds} newUploadedDocIds={newUploadedDocIds} setNewUploadedDocIds={setNewUploadedDocIds}
                            contextType={contextType}
                            stepType={stepType}
                            isOldAccount={processAccountData?.isOldAccount}
                        />
                        {/* {!isJobProcessing ? <div className={styles.switchToNetBankingText} onClick={() => {
                            setStatementLocal(StatementStepType.net_banking);
                        }}>
                            <div className={styles.iconContainer}>
                                <BackIcon />
                            </div>
                            <div className={styles.text}>
                                Fetch bank statements with net-banking.
                            </div>
                        </div> : null} */}
                    </>
                    {/* } */}
                </div>
                <Field name={`${fieldName}.accountError`}>
                    {(props) => {
                        return (
                            <div id={`${fieldName}.accountError`} className={styles.errorText}>
                                {props.meta.touched && props.meta.error ? props.meta.error : null}
                            </div>
                        )
                    }}
                </Field>
            </div>
            <ConfirmDialog
                modalStyles={{ maxWidth: '450px' }}
                showButton={false}
                visible={showDeleteConfirmation}
                toggle={() => {
                    setShowDeleteConfirmation(!showDeleteConfirmation);
                }}
                showCloseBtn={false}
                size={"md"}
            >
                <div className={styles.confirmationContainer}>
                    <div className={styles.topContainer}>
                        <div className={styles.iconContainer}>
                            <Exclamation />
                        </div>
                        <div className={styles.title}>
                            Are You Sure You Want to Delete This Account?
                            <br />
                            <br />
                        </div>

                        {/* <div className={styles.warningText}>
                            {`To continue, please add taxes for your products.`}
                            <br />
                            <br />
                        </div> */}
                    </div>

                    <div className={styles.bottomContainer}>
                        <MeritoButton onClick={() => {
                            setShowDeleteConfirmation(false);
                        }} size="lg" isRounded={true} btnType="button" type="secondary-2" btnText={'Cancel'} />
                        <MeritoButton size="lg" onClick={() => {
                            remove('bankStatements', fieldIndex)
                            setShowDeleteConfirmation(false);
                        }} isRounded={true} btnType="button" type="primary-1" btnText="Delete" />
                    </div>
                </div>
            </ConfirmDialog>


        </>
    );
}

export default BankStatements